import React, { useState } from 'react';
import {
    Box,
    Typography,
    Avatar,
    Button,
    Chip,
    TextField,
    IconButton,
    Paper,
    Divider,
    MenuItem,
    InputAdornment
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';

const ServiceDetailView = ({
    service,
    onRequestPackage,
    onRemove,
    requestStatus = null // 'sent' | null
}) => {
    // State for selection
    const [selectedIdx, setSelectedIdx] = useState(null); // 0 = custom package, 1...N = package idx+1
    const [customDeliverables, setCustomDeliverables] = useState(['']);
    const [customType, setCustomType] = useState('monetary');
    const [customAmount, setCustomAmount] = useState('');
    const [customUnit, setCustomUnit] = useState('');
    const [customNotes, setCustomNotes] = useState('');
    const [requestSent, setRequestSent] = useState(false);
    const [customSubmitting, setCustomSubmitting] = useState(false);

    const [customEventStyle, setCustomEventStyle] = useState('');
    const [customDietary, setCustomDietary] = useState([]);
    const [customBeverageService, setCustomBeverageService] = useState('');
    const [customBudget, setCustomBudget] = useState('');

    const [isCustomSelected, setIsCustomSelected] = useState(selectedIdx === 0);

    // const [customDeliverables, setCustomDeliverables] = useState(['']);
    // const [customType, setCustomType] = useState('monetary');
    // const [customAmount, setCustomAmount] = useState('');
    // const [customUnit, setCustomUnit] = useState('');

    const serviceType = service.addonType || service.type;
    const isCatering = serviceType?.toLowerCase().includes('catering');
    // Helper: is custom selected
    // const isCustomSelected = selectedIdx === 0;
    // Helper: is a package selected
    const isPackageSelected = selectedIdx !== null && selectedIdx > 0;
    // Helper: selected package
    const selectedPackage = isPackageSelected ? service.packages[selectedIdx - 1] : null;

    // Custom package form handlers
    const handleDeliverableChange = (idx, value) => {
        setCustomDeliverables(prev => prev.map((d, i) => i === idx ? value : d));
    };
    const handleAddDeliverable = () => setCustomDeliverables(prev => [...prev, '']);
    const handleRemoveDeliverable = (idx) => setCustomDeliverables(prev => prev.filter((_, i) => i !== idx));

    // Request Service handler
    const handleRequest = async () => {
        if (isCustomSelected) {
            setCustomSubmitting(true);
            const customPackage = {
                companyId: service.companyId || service.id,
                name: service.name,
                custom: true,
                deliverables: customDeliverables.filter(Boolean),
                type: customType,
                amount: customAmount,
                unit: customUnit,
                notes: customNotes
            };
            await onRequestPackage(customPackage);
            setCustomSubmitting(false);
            setRequestSent(true);
        } else if (isPackageSelected) {
            await onRequestPackage(selectedPackage);
            setRequestSent(true);
        }
    };

    // Remove handler
    const handleRemove = () => {
        if (onRemove) onRemove(service);
    };

    const handleSelectedServicePackage = () => {
        if (isCustomSelected) {
            setIsCustomSelected(false)
            setSelectedIdx(null)
        } else {
            setIsCustomSelected(true)
            setSelectedIdx(0)
        }
    }

    // Disable request button logic
    const canRequest = (
        (isCustomSelected || isPackageSelected) && 
        !requestSent && !customSubmitting);

    // Starting price (from packages)
    const startingPrice = service.packages && service.packages.length > 0
        ? service.packages.reduce((min, p) => (p.price && (min === null || p.price < min)) ? p.price : min, null)
        : null;

    // --- UI ---
    return (
        <Box sx={{ display: 'flex', gap: 4, width: '100%' }}>
            {/* Left: Company Info & Actions */}
            <Box sx={{ minWidth: 320, maxWidth: 340, flex: '0 0 340px', display: 'flex', flexDirection: 'column', alignItems: 'center', p: 3, borderRight: '1px solid #eee' }}>
                <Avatar src={service.logo || service.logoUrl} sx={{ width: 80, height: 80, mb: 2 }} />
                <Typography variant="h5" fontWeight={600} align="center">{service.name}</Typography>
                {startingPrice && (
                    <Chip label={`Starting at $${startingPrice.toLocaleString()}`} color="primary" sx={{ mt: 1, mb: 1 }} />
                )}
                <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1, mb: 2 }}>{service.description || service.desc}</Typography>
                <Box sx={{ width: '100%', mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Button
                        variant="contained"
                        color={requestSent ? 'success' : 'primary'}
                        disabled={!canRequest}
                        onClick={handleRequest}
                        sx={{ fontWeight: 600 }}
                        fullWidth
                    >
                        {requestSent ? 'Request Sent' : 'Request Service'}
                    </Button>
                    <Button
                        variant="outlined"
                        color="error"
                        onClick={handleRemove}
                        fullWidth
                        sx={{ fontWeight: 600 }}
                    >
                        Remove
                    </Button>
                </Box>
            </Box>
            {/* Right: Packages Row */}
            {isCatering ? (
                <Box sx={{ flex: 1, overflowX: 'auto', display: 'flex', alignItems: 'flex-start', gap: 3, py: 2 }}>
                    <Paper
                        elevation={isCustomSelected ? 6 : 1}
                        sx={{ minWidth: 320, maxWidth: 340, p: 3, borderRadius: 3, border: isCustomSelected ? '2px solid #1976d2' : '1px solid #eee', boxShadow: isCustomSelected ? 4 : 1, flex: '0 0 340px', cursor: 'pointer', background: isCustomSelected ? '#f0f6ff' : '#fff' }}
                        onClick={() => setSelectedIdx(0)}
                    >
                    <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>Custom Catering Request</Typography>
                    <Divider sx={{ mb: 2 }} />
                    
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Event Style</Typography>
                        <TextField
                            select
                            value={customEventStyle || ''}
                            onChange={e => setCustomEventStyle(e.target.value)}
                            fullWidth
                            size="small"
                            onClick={e => e.stopPropagation()}
                        >
                            <MenuItem value="formal">Formal Dinner</MenuItem>
                            <MenuItem value="casual">Casual Gathering</MenuItem>
                            <MenuItem value="cocktail">Cocktail Party</MenuItem>
                            <MenuItem value="buffet">Buffet Style</MenuItem>
                            <MenuItem value="family">Family Style</MenuItem>
                        </TextField>
                    </Box>
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Dietary Requirements</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {['Vegetarian', 'Vegan', 'Gluten-Free', 'Kosher', 'Halal'].map((diet) => (
                                <Chip
                                    key={diet}
                                    label={diet}
                                    color={customDietary?.includes(diet) ? 'primary' : 'default'}
                                    onClick={e => {
                                        e.stopPropagation();
                                        setCustomDietary(prev => 
                                            prev?.includes(diet) 
                                                ? prev.filter(d => d !== diet)
                                                : [...(prev || []), diet]
                                        );
                                    }}
                                />
                            ))}
                        </Box>
                    </Box>
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Beverage Service</Typography>
                        <TextField
                            select
                            value={customBeverageService || ''}
                            onChange={e => setCustomBeverageService(e.target.value)}
                            fullWidth
                            size="small"
                            onClick={e => e.stopPropagation()}
                        >
                            <MenuItem value="full">Full Bar Service</MenuItem>
                            <MenuItem value="beer_wine">Beer & Wine Only</MenuItem>
                            <MenuItem value="non_alcoholic">Non-Alcoholic Only</MenuItem>
                            <MenuItem value="none">No Beverage Service</MenuItem>
                        </TextField>
                    </Box>
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Budget Range (per person)</Typography>
                        <TextField
                            type="number"
                            value={customBudget || ''}
                            onChange={e => setCustomBudget(e.target.value)}
                            fullWidth
                            size="small"
                            InputProps={{
                                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            }}
                            onClick={e => e.stopPropagation()}
                        />
                    </Box>
                    <TextField
                        label="Special Requests / Notes"
                        value={customNotes}
                        onChange={e => setCustomNotes(e.target.value)}
                        multiline
                        minRows={2}
                        fullWidth
                        sx={{ mb: 2 }}
                        onClick={e => e.stopPropagation()}
                    />

                    <Button
                        variant={isCustomSelected ? 'contained' : 'outlined'}
                        color="primary"
                        fullWidth
                        sx={{ mt: 1, fontWeight: 600 }}
                        // disabled={isCustomSelected}
                        onClick={handleSelectedServicePackage}
                    >
                        {isCustomSelected ? 'Custom Package Selected' : 'Select Custom Package'}
                    </Button>

                    </Paper>
                </Box>
            ) : (
            <Box sx={{ flex: 1, overflowX: 'auto', display: 'flex', alignItems: 'flex-start', gap: 3, py: 2 }}>
                {/* Custom Package Card */}
                <Paper
                    elevation={isCustomSelected ? 6 : 1}
                    sx={{ minWidth: 320, maxWidth: 340, p: 3, borderRadius: 3, border: isCustomSelected ? '2px solid #1976d2' : '1px solid #eee', boxShadow: isCustomSelected ? 4 : 1, flex: '0 0 340px', cursor: 'pointer', background: isCustomSelected ? '#f0f6ff' : '#fff' }}
                    onClick={() => setSelectedIdx(0)}
                >
                    <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>Custom Package</Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Deliverables</Typography>
                    {customDeliverables.map((d, idx) => (
                        <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <TextField
                                value={d}
                                onChange={e => handleDeliverableChange(idx, e.target.value)}
                                placeholder={`Deliverable #${idx + 1}`}
                                size="small"
                                fullWidth
                                onClick={e => e.stopPropagation()}
                            />
                            {customDeliverables.length > 1 && (
                                <IconButton onClick={e => { e.stopPropagation(); handleRemoveDeliverable(idx); }} size="small"><DeleteIcon /></IconButton>
                            )}
                        </Box>
                    ))}
                    <Button startIcon={<AddIcon />} onClick={e => { e.stopPropagation(); handleAddDeliverable(); }} size="small" sx={{ mb: 2 }}>Add Deliverable</Button>
                    <Typography variant="subtitle2">Type</Typography>
                    <Box sx={{ display: 'flex', gap: 2, mt: 1, mb: 2 }}>
                        <Chip
                            label="Monetary"
                            color={customType === 'monetary' ? 'primary' : 'default'}
                            onClick={e => { e.stopPropagation(); setCustomType('monetary'); }}
                        />
                        <Chip
                            label="In-Kind"
                            color={customType === 'inkind' ? 'primary' : 'default'}
                            onClick={e => { e.stopPropagation(); setCustomType('inkind'); }}
                        />
                        <Chip
                            label="Both"
                            color={customType === 'both' ? 'primary' : 'default'}
                            onClick={e => { e.stopPropagation(); setCustomType('both'); }}
                        />
                    </Box>
                    {(customType === 'inkind' || customType === 'both') && (
                        <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
                            <TextField
                                label="Amount"
                                value={customAmount}
                                onChange={e => setCustomAmount(e.target.value)}
                                size="small"
                                type="number"
                                sx={{ width: 120 }}
                                onClick={e => e.stopPropagation()}
                            />
                            <TextField
                                label="Unit (e.g. cases, hours, swag bags)"
                                value={customUnit}
                                onChange={e => setCustomUnit(e.target.value)}
                                size="small"
                                sx={{ width: 200 }}
                                onClick={e => e.stopPropagation()}
                            />
                        </Box>
                    )}
                    <TextField
                        label="Notes / Special Requests"
                        value={customNotes}
                        onChange={e => setCustomNotes(e.target.value)}
                        multiline
                        minRows={2}
                        fullWidth
                        sx={{ mb: 2 }}
                        onClick={e => e.stopPropagation()}
                    />
                    <Button
                        variant={isCustomSelected ? 'contained' : 'outlined'}
                        color="primary"
                        fullWidth
                        sx={{ mt: 1, fontWeight: 600 }}
                        // disabled={isCustomSelected}
                        onClick={handleSelectedServicePackage}
                    >
                        {isCustomSelected ? 'Custom Package Selected' : 'Select Custom Package'}
                    </Button>
                </Paper>
                {/* Other Packages */}
                {service.packages && service.packages.map((pkg, idx) => (
                    <Paper
                        key={pkg.packageId}
                        elevation={selectedIdx === idx + 1 ? 6 : 1}
                        sx={{ minWidth: 320, maxWidth: 340, p: 3, borderRadius: 3, border: selectedIdx === idx + 1 ? '2px solid #1976d2' : '1px solid #eee', boxShadow: selectedIdx === idx + 1 ? 4 : 1, flex: '0 0 340px', cursor: 'pointer', background: selectedIdx === idx + 1 ? '#f0f6ff' : '#fff' }}
                        onClick={() => setSelectedIdx(idx + 1)}
                    >
                        <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>{pkg.name} {pkg.price ? `- $${pkg.price.toLocaleString()}` : ''}</Typography>
                        <Divider sx={{ mb: 2 }} />
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{pkg.description}</Typography>
                        {pkg.packageAddons && pkg.packageAddons.length > 0 && (
                            <Box sx={{ mt: 1 }}>
                                <Typography variant="subtitle2">Deliverables:</Typography>
                                <ul style={{ margin: 0, paddingLeft: 18 }}>
                                    {pkg.packageAddons.map(addon => (
                                        <li key={addon.packageAddonId}>
                                            <Typography variant="body2">{addon.name || addon.description}</Typography>
                                        </li>
                                    ))}
                                </ul>
                            </Box>
                        )}
                        <Button
                            variant={selectedIdx === idx + 1 ? 'contained' : 'outlined'}
                            color="primary"
                            fullWidth
                            sx={{ mt: 2, fontWeight: 600 }}
                            disabled={selectedIdx === idx + 1}
                        >
                            {selectedIdx === idx + 1 ? 'Package Selected' : 'Select Package'}
                        </Button>
                    </Paper>
                ))}
            </Box>
            )}
        </Box>
    );
};

export default ServiceDetailView; 